import { useState, useEffect } from 'react';
import { companyData, cases, commonPatterns } from './data';

const S = {
  mono: "'IBM Plex Mono', 'Courier New', monospace",
  display: "'Playfair Display', Georgia, serif",
  body: "'Source Serif 4', Georgia, serif",
};

function Tag({ children, color = '#e63946', style = {} }) {
  return (
    <span style={{
      border: `1px solid ${color}55`,
      color,
      padding: '4px 12px',
      fontSize: 11,
      letterSpacing: 2,
      fontFamily: S.mono,
      textTransform: 'uppercase',
      display: 'inline-block',
      ...style,
    }}>
      {children}
    </span>
  );
}

function CaseCard({ c, onClick }) {
  const [hover, setHover] = useState(false);
  return (
    <div
      onClick={onClick}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        background: hover ? '#111' : '#0d0d0d',
        padding: 32,
        cursor: 'pointer',
        borderTop: `3px solid ${c.color}`,
        borderRight: '1px solid #1a1a1a',
        borderBottom: '1px solid #1a1a1a',
        borderLeft: '1px solid #1a1a1a',
        transition: 'all 0.2s',
        transform: hover ? 'translateY(-2px)' : 'none',
        boxShadow: hover ? `0 8px 32px ${c.color}11` : 'none',
      }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 20 }}>
        <div style={{
          width: 48, height: 48, borderRadius: '50%',
          background: c.color + '18',
          border: `2px solid ${c.color}44`,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontSize: 13, fontFamily: S.mono, color: c.color, fontWeight: 700,
        }}>{c.avatar}</div>
        <Tag color={c.color}>Case #{c.id}</Tag>
      </div>

      <div style={{ marginBottom: 8, fontSize: 18, color: '#e8e0d4', fontFamily: S.display }}>{c.role}</div>
      <div style={{ fontSize: 12, color: '#555', fontFamily: S.mono, marginBottom: 16 }}>{c.name} • {c.date} • {c.duration}</div>

      <p style={{ fontSize: 14, color: '#888', lineHeight: 1.7, marginBottom: 24 }}>{c.summary}</p>

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end' }}>
        <div>
          <div style={{ fontSize: 28, fontWeight: 700, color: '#e63946', fontFamily: S.display }}>{c.amount}</div>
          <div style={{ fontSize: 11, color: '#555', fontFamily: S.mono, marginTop: 2 }}>UNPAID AMOUNT</div>
        </div>
        <div style={{
          fontSize: 11, fontFamily: S.mono,
          color: c.color, border: `1px solid ${c.color}44`,
          padding: '6px 14px', letterSpacing: 1,
        }}>
          VIEW FULL CASE →
        </div>
      </div>
    </div>
  );
}

function CaseModal({ c, onClose }) {
  useEffect(() => {
    const handler = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [onClose]);

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0,
        background: 'rgba(0,0,0,0.88)',
        zIndex: 1000,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: '20px',
      }}
    >
      <div
        className="modal-in"
        onClick={e => e.stopPropagation()}
        style={{
          background: '#0d0d0d',
          border: `1px solid #1a1a1a`,
          borderTop: `4px solid ${c.color}`,
          maxWidth: 720, width: '100%',
          maxHeight: '88vh', overflowY: 'auto',
          padding: 0,
        }}
      >
        {/* Modal Header */}
        <div style={{
          padding: '28px 36px 24px',
          borderBottom: '1px solid #1a1a1a',
          display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start',
        }}>
          <div>
            <Tag color="#555" style={{ marginBottom: 10 }}>CASE #{c.id} — EMPLOYEE REPORT</Tag>
            <div style={{ fontSize: 24, fontFamily: S.display, color: '#e8e0d4', marginTop: 8 }}>{c.role}</div>
            <div style={{ fontSize: 13, color: '#555', fontFamily: S.mono, marginTop: 6 }}>
              {c.name} &nbsp;•&nbsp; {c.date} &nbsp;•&nbsp; Duration: {c.duration}
            </div>
          </div>
          <button
            onClick={onClose}
            style={{
              background: 'none', border: '1px solid #2a2a2a',
              color: '#666', padding: '8px 16px', cursor: 'pointer',
              fontFamily: S.mono, fontSize: 12, letterSpacing: 1,
              flexShrink: 0, marginLeft: 20,
            }}
          >ESC / CLOSE</button>
        </div>

        <div style={{ padding: '32px 36px' }}>
          {/* Amount */}
          <div style={{
            padding: '20px 24px', background: '#e63946' + '11',
            border: `1px solid #e6394622`,
            marginBottom: 32,
            display: 'flex', alignItems: 'center', gap: 24,
          }}>
            <div>
              <div style={{ fontSize: 40, fontWeight: 700, color: '#e63946', fontFamily: S.display }}>{c.amount}</div>
              <div style={{ fontSize: 11, color: '#e6394688', fontFamily: S.mono, letterSpacing: 2 }}>TOTAL UNPAID AS OF JAN 2026</div>
            </div>
            <div style={{ fontSize: 13, color: '#777', lineHeight: 1.7, maxWidth: 360 }}>
              Yeh amount delivery ke baad se outstanding hai. Kaam accept hua, use bhi hua — payment kabhi nahi aayi.
            </div>
          </div>

          {/* Full Account */}
          <div style={{ marginBottom: 32 }}>
            <div style={{ fontSize: 11, color: '#555', fontFamily: S.mono, letterSpacing: 2, marginBottom: 16 }}>FULL ACCOUNT</div>
            {c.details.map((para, i) => (
              <p key={i} style={{ fontSize: 14, color: '#999', lineHeight: 1.85, marginBottom: 14 }}>{para}</p>
            ))}
          </div>

          {/* Timeline */}
          <div style={{ marginBottom: 32 }}>
            <div style={{ fontSize: 11, color: '#555', fontFamily: S.mono, letterSpacing: 2, marginBottom: 20 }}>TIMELINE</div>
            <div style={{ position: 'relative', paddingLeft: 24 }}>
              <div style={{
                position: 'absolute', left: 7, top: 8, bottom: 8,
                width: 1, background: '#1f1f1f',
              }} />
              {c.timeline.map((t, i) => (
                <div key={i} style={{ display: 'flex', gap: 20, marginBottom: 16, position: 'relative' }}>
                  <div style={{
                    position: 'absolute', left: -20, top: 5,
                    width: 8, height: 8, borderRadius: '50%',
                    background: i === c.timeline.length - 1 ? '#e63946' : '#2a2a2a',
                    border: `1px solid ${i === c.timeline.length - 1 ? '#e63946' : '#333'}`,
                  }} />
                  <div style={{ fontSize: 11, color: '#555', fontFamily: S.mono, minWidth: 80 }}>{t.date}</div>
                  <div style={{ fontSize: 13, color: '#999' }}>{t.event}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Two column: Red Flags + Evidence */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20, marginBottom: 32 }}>
            <div>
              <div style={{ fontSize: 11, color: '#555', fontFamily: S.mono, letterSpacing: 2, marginBottom: 14 }}>⚠ RED FLAGS</div>
              {c.redFlags.map((flag, i) => (
                <div key={i} style={{
                  padding: '10px 14px',
                  background: '#111', borderLeft: `2px solid ${c.color}44`,
                  marginBottom: 6,
                }}>
                  <span style={{ fontSize: 13, color: '#888' }}>{flag}</span>
                </div>
              ))}
            </div>
            <div>
              <div style={{ fontSize: 11, color: '#555', fontFamily: S.mono, letterSpacing: 2, marginBottom: 14 }}>📎 EVIDENCE AVAILABLE</div>
              {c.evidence.map((ev, i) => (
                <div key={i} style={{
                  padding: '10px 14px',
                  background: '#111', borderLeft: '2px solid #2a2a2a',
                  marginBottom: 6, fontSize: 13, color: '#777', fontFamily: S.mono,
                }}>{ev}</div>
              ))}
            </div>
          </div>

          {/* Disclaimer */}
          <div style={{
            padding: 16, background: '#080808',
            fontSize: 11, color: '#3a3a3a', fontFamily: S.mono, lineHeight: 1.8,
            borderTop: '1px solid #111',
          }}>
            DISCLAIMER: Yeh ek personal experience hai jo awareness ke liye share kiya gaya hai. Yeh website kisi bhi ongoing legal proceeding ka hissa nahi hai. Readers apna judgment use karein.
          </div>
        </div>
      </div>
    </div>
  );
}

function CompanyInfo() {
  const fields = [
    ['CIN', companyData.cin],
    ['ROC Code', companyData.roc],
    ['Registration Number', companyData.regNo],
    ['Company Status (eFiling)', companyData.status],
    ['Date of Incorporation', companyData.incorporated],
    ['Last Updated', companyData.lastUpdated],
    ['Category', companyData.category],
    ['Sub-Category', companyData.subCategory],
    ['Class', companyData.classOfCompany],
    ['Authorized Capital', companyData.authorizedCapital],
    ['Paid-Up Capital', companyData.paidUpCapital],
    ['Listed Status', companyData.listed],
    ['Email', companyData.email],
    ['Registered Address', companyData.address],
  ];

  return (
    <div className="fade-in">
      <div style={{
        padding: '24px 28px', background: '#0d0d0d',
        border: '1px solid #1a1a1a', marginBottom: 24,
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        flexWrap: 'wrap', gap: 12,
      }}>
        <div>
          <div style={{ fontSize: 13, color: '#555', fontFamily: S.mono, marginBottom: 6 }}>REGISTERED COMPANY NAME</div>
          <div style={{ fontSize: 20, color: '#e8e0d4', fontFamily: S.display }}>{companyData.name}</div>
        </div>
        <Tag color="#2a9d8f">MCA STATUS: ACTIVE</Tag>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: 1, background: '#111', marginBottom: 24 }}>
        {fields.map(([key, val]) => (
          <div key={key} style={{ background: '#0d0d0d', padding: '18px 22px' }}>
            <div style={{ fontSize: 10, color: '#444', fontFamily: S.mono, letterSpacing: 2, marginBottom: 6, textTransform: 'uppercase' }}>{key}</div>
            <div style={{ fontSize: 13, color: '#bbb', wordBreak: 'break-all', lineHeight: 1.5 }}>{val}</div>
          </div>
        ))}
      </div>

      <div style={{ padding: '0 0 24px' }}>
        <div style={{ fontSize: 11, color: '#555', fontFamily: S.mono, letterSpacing: 2, marginBottom: 16 }}>DIRECTORS / SIGNATORIES (AS PER MCA)</div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: 1, background: '#111' }}>
          {companyData.directors.map((d) => (
            <div key={d.din} style={{ background: '#0d0d0d', padding: '20px 24px' }}>
              <div style={{ fontSize: 16, color: '#e8e0d4', fontFamily: S.display, marginBottom: 8 }}>{d.name}</div>
              <div style={{ fontSize: 11, color: '#555', fontFamily: S.mono }}>DIN: {d.din}</div>
              <div style={{ fontSize: 11, color: '#555', fontFamily: S.mono }}>Director Since: {d.since}</div>
            </div>
          ))}
        </div>
      </div>

      <div style={{
        padding: '20px 24px', background: '#080808',
        border: '1px solid #111',
        fontSize: 12, color: '#444', fontFamily: S.mono, lineHeight: 1.8,
      }}>
        SOURCE: Data above is sourced from IndiaFilings.com and Ministry of Corporate Affairs (MCA) public portal. CIN: U47912UP2025PTC220541. Last verified: January 2026.
      </div>
    </div>
  );
}

function PatternsSection() {
  return (
    <div className="fade-in">
      <div style={{ marginBottom: 40 }}>
        <p style={{ fontSize: 15, color: '#888', lineHeight: 1.8, maxWidth: 680 }}>
          Yeh patterns multiple employees ne independently aur alag-alag time pe report kiye hain. 
          Agar aap bhi in patterns ko notice kar rahe hain — kisi bhi collaboration mein — toh savdhan rahein.
        </p>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: 1, background: '#111', marginBottom: 40 }}>
        {commonPatterns.map((p) => (
          <div key={p.step} style={{ background: '#0d0d0d', padding: 32, position: 'relative', overflow: 'hidden' }}>
            <div style={{
              position: 'absolute', top: 16, right: 20,
              fontSize: 48, color: '#ffffff08', fontFamily: S.mono, fontWeight: 700,
            }}>{p.step}</div>
            <div style={{ fontSize: 28, marginBottom: 16 }}>{p.icon}</div>
            <div style={{ fontSize: 16, color: '#e8e0d4', marginBottom: 10, fontFamily: S.display }}>{p.title}</div>
            <p style={{ fontSize: 13, color: '#777', lineHeight: 1.8, margin: 0 }}>{p.desc}</p>
          </div>
        ))}
      </div>

      <div style={{
        padding: 32, background: '#0d0d0d',
        borderLeft: '4px solid #e63946',
        marginBottom: 24,
      }}>
        <Tag color="#e63946" style={{ marginBottom: 16 }}>⚠ INVESTORS & COLLABORATORS KE LIYE</Tag>
        <p style={{ fontSize: 15, color: '#999', lineHeight: 1.9, margin: '16px 0 0' }}>
          Agar aap Glazzarto Technologies ya iske founders — <strong style={{ color: '#ccc' }}>Anjali Singh</strong>, 
          <strong style={{ color: '#ccc' }}> Surya Pratap Singh Chauhan</strong>, ya 
          <strong style={{ color: '#ccc' }}> Mayank Pratap Singh Chauhan</strong> — ke saath kisi bhi business deal, 
          investment, ya freelance collaboration ki planning kar rahe hain, toh humari guzarish hai ki pehle apna 
          thorough due diligence karein. Company ka paid-up capital sirf <strong style={{ color: '#f4a261' }}>₹10,000</strong> hai — 
          financial capacity verify zaroor karein. Yeh portal sirf public awareness ke liye hai.
        </p>
      </div>

      <div style={{
        padding: '20px 24px', background: '#080808',
        border: '1px solid #111',
        fontSize: 12, color: '#444', fontFamily: S.mono, lineHeight: 1.8,
      }}>
        NOTE: Patterns described above are based on personal experiences of ex-employees. This is not legal advice. 
        All readers should conduct their own research and consult professionals as needed.
      </div>
    </div>
  );
}

export default function App() {
  const [activeTab, setActiveTab] = useState('cases');
  const [selectedCase, setSelectedCase] = useState(null);

  const totalUnpaid = cases.reduce((sum, c) => sum + c.amountNum, 0);

  const tabs = [
    { id: 'cases', label: 'Employee Cases' },
    { id: 'company', label: 'Company Info' },
    { id: 'patterns', label: 'Patterns & Red Flags' },
  ];

  return (
    <div style={{ minHeight: '100vh', background: '#0a0a0a' }}>

      {/* Top bar */}
      <div style={{
        background: '#e63946',
        padding: '8px 0',
        textAlign: 'center',
        fontSize: 11, fontFamily: S.mono, letterSpacing: 2,
        color: '#fff',
      }}>
        ⚠ PUBLIC AWARENESS NOTICE — NOT AFFILIATED WITH GLAZZARTO TECHNOLOGIES ⚠
      </div>

      {/* Sticky Nav */}
      <nav style={{
        borderBottom: '1px solid #1a1a1a',
        padding: '0 40px',
        background: 'rgba(10,10,10,0.95)',
        backdropFilter: 'blur(12px)',
        position: 'sticky', top: 0, zIndex: 100,
      }}>
        <div style={{
          maxWidth: 1100, margin: '0 auto',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          height: 60,
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <div style={{
              width: 8, height: 8, borderRadius: '50%',
              background: '#e63946',
              animation: 'pulse-dot 2s infinite',
            }} />
            <span style={{ fontSize: 13, letterSpacing: 3, color: '#666', fontFamily: S.mono, textTransform: 'uppercase' }}>
              Awareness Portal
            </span>
          </div>
          <span style={{ fontSize: 11, color: '#333', fontFamily: S.mono, letterSpacing: 1 }}>
            CIN: U47912UP2025PTC220541
          </span>
        </div>
      </nav>

      {/* Hero */}
      <div style={{
        maxWidth: 1100, margin: '0 auto', padding: '72px 40px 56px',
        borderBottom: '1px solid #111',
      }}>
        <div className="fade-in">
          <Tag color="#e63946" style={{ marginBottom: 24 }}>Public Notice</Tag>
          <h1 style={{
            fontSize: 'clamp(36px, 7vw, 80px)',
            fontFamily: S.display,
            fontWeight: 900,
            lineHeight: 1.02,
            margin: '0 0 24px',
            color: '#f0ebe4',
            letterSpacing: -1,
          }}>
            Glazzarto Technologies<br />
            <span style={{ color: '#e63946' }}>Unpaid Work</span><br />
            <span style={{ fontSize: '0.6em', color: '#666' }}>Employee Awareness Portal</span>
          </h1>
          <p style={{ fontSize: 17, color: '#777', maxWidth: 620, lineHeight: 1.8, margin: '0 0 40px' }}>
            Yeh website un logon ke liye hai jo Glazzarto Technologies Private Limited ke saath kaam karne ka soch rahe hain — 
            freelancers, developers, designers, ya investors. Hum apne personal experiences share kar rahe hain taaki 
            aap ek informed decision le sakein.
          </p>

          <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', alignItems: 'center' }}>
            <div style={{ padding: '16px 24px', background: '#111', borderTop: '2px solid #e63946' }}>
              <div style={{ fontSize: 28, fontFamily: S.display, fontWeight: 700, color: '#e63946' }}>
                ₹{totalUnpaid.toLocaleString('en-IN')}+
              </div>
              <div style={{ fontSize: 10, color: '#555', fontFamily: S.mono, letterSpacing: 2, marginTop: 4 }}>TOTAL UNPAID</div>
            </div>
            <div style={{ padding: '16px 24px', background: '#111', borderTop: '2px solid #f4a261' }}>
              <div style={{ fontSize: 28, fontFamily: S.display, fontWeight: 700, color: '#f4a261' }}>3+</div>
              <div style={{ fontSize: 10, color: '#555', fontFamily: S.mono, letterSpacing: 2, marginTop: 4 }}>CASES REPORTED</div>
            </div>
            <div style={{ padding: '16px 24px', background: '#111', borderTop: '2px solid #2a9d8f' }}>
              <div style={{ fontSize: 28, fontFamily: S.display, fontWeight: 700, color: '#2a9d8f' }}>2025</div>
              <div style={{ fontSize: 10, color: '#555', fontFamily: S.mono, letterSpacing: 2, marginTop: 4 }}>INCORPORATED</div>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div style={{ maxWidth: 1100, margin: '0 auto', padding: '0 40px' }}>
        {/* Tabs */}
        <div style={{ display: 'flex', borderBottom: '1px solid #1a1a1a', marginTop: 48 }}>
          {tabs.map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              style={{
                background: 'none', border: 'none',
                padding: '14px 28px',
                fontSize: 12, letterSpacing: 2, fontFamily: S.mono,
                cursor: 'pointer', textTransform: 'uppercase',
                color: activeTab === tab.id ? '#e8e0d4' : '#444',
                borderBottom: activeTab === tab.id ? '2px solid #e63946' : '2px solid transparent',
                marginBottom: '-1px',
                transition: 'color 0.2s',
              }}
            >
              {tab.label}
            </button>
          ))}
        </div>

        <div style={{ padding: '48px 0 80px' }}>
          {activeTab === 'cases' && (
            <div className="fade-in">
              <p style={{ fontSize: 12, color: '#444', fontFamily: S.mono, letterSpacing: 1, marginBottom: 32 }}>
                // {cases.length} CASES REPORTED — CLICK TO VIEW FULL DETAILS
              </p>
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))',
                gap: 16,
              }}>
                {cases.map(c => (
                  <CaseCard key={c.id} c={c} onClick={() => setSelectedCase(c)} />
                ))}
              </div>

              <div style={{
                marginTop: 40, padding: '20px 24px',
                border: '1px solid #111', background: '#080808',
                fontSize: 11, color: '#3a3a3a', fontFamily: S.mono, lineHeight: 1.9,
              }}>
                ⚖ DISCLAIMER: Cases above represent personal experiences shared anonymously by ex-employees of Glazzarto Technologies. 
                This website is not part of any legal proceeding. Information is shared for public awareness purposes only. 
                Readers are encouraged to conduct their own due diligence. Content may be updated as more experiences are shared.
              </div>
            </div>
          )}

          {activeTab === 'company' && <CompanyInfo />}
          {activeTab === 'patterns' && <PatternsSection />}
        </div>
      </div>

      {/* Footer */}
      <div style={{
        borderTop: '1px solid #111',
        padding: '32px 40px',
        textAlign: 'center',
      }}>
        <div style={{ fontSize: 11, color: '#333', fontFamily: S.mono, letterSpacing: 1, lineHeight: 2 }}>
          <div>GLAZZARTO AWARENESS PORTAL — NOT AFFILIATED WITH GLAZZARTO TECHNOLOGIES PVT LTD</div>
          <div>CIN: U47912UP2025PTC220541 | SOURCE: MCA / INDIAFILINGS (PUBLIC DATA)</div>
          <div style={{ marginTop: 8, color: '#222' }}>
            Yeh website sirf public awareness ke liye hai. Koi bhi legal claim ya defamation ka intent nahi hai.
          </div>
        </div>
      </div>

      {/* Modal */}
      {selectedCase && (
        <CaseModal c={selectedCase} onClose={() => setSelectedCase(null)} />
      )}
    </div>
  );
}
